import PropTypes from 'prop-types';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import SimpleBar from 'simplebar-react';

/**
 * 全局滚动容器：overlay 纵向滚动条，落在父级已有右侧留白（见 index.css .app-scroll-area）。
 * 嵌套在页面主滚动内时使用 className 含 `app-scroll-area--embedded`，轨道留在组件内。
 */
const AppScrollArea = forwardRef(({
	children,
	className = '',
	style,
	maxHeight,
	fillViewport = false,
	autoHide = true,
	highlightOnScroll = false,
	scrollIdleMs = 90,
	onMouseLeave,
	scrollableNodeProps: scrollableNodePropsProp = {},
}, ref) => {
	const simpleBarRef = useRef(null);

	useImperativeHandle(ref, () => ({
		recalculate: () => simpleBarRef.current?.recalculate?.(),
		getScrollElement: () => simpleBarRef.current?.getScrollElement?.() ?? null,
	}));
	const [scrolling, setScrolling] = useState(false);
	const scrollIdleTimerRef = useRef(null);

	const handleScroll = useCallback(() => {
		if (!highlightOnScroll) return;
		setScrolling(true);
		if (scrollIdleTimerRef.current) {
			clearTimeout(scrollIdleTimerRef.current);
		}
		scrollIdleTimerRef.current = setTimeout(() => setScrolling(false), scrollIdleMs);
	}, [highlightOnScroll, scrollIdleMs]);

	useEffect(
		() => () => {
			if (scrollIdleTimerRef.current) {
				clearTimeout(scrollIdleTimerRef.current);
			}
		},
		[],
	);

	const mergedClassName = ['app-scroll-area', className, highlightOnScroll && scrolling ? 'app-scroll-area--scrolling' : ''].filter(Boolean).join(' ');

	const rootStyle = {
		maxHeight,
		minHeight: 0,
		...(fillViewport && maxHeight != null ? { height: maxHeight } : {}),
		...style,
	};

	const scrollableNodeProps = {
		...scrollableNodePropsProp,
		...(onMouseLeave ? { onMouseLeave } : {}),
		...(highlightOnScroll
			? {
					onScroll: (event) => {
						handleScroll();
						scrollableNodePropsProp.onScroll?.(event);
					},
				}
			: {}),
	};

	return (
		<SimpleBar ref={simpleBarRef} className={mergedClassName} style={rootStyle} autoHide={autoHide} scrollableNodeProps={scrollableNodeProps}>
			{children}
		</SimpleBar>
	);
});

AppScrollArea.displayName = 'AppScrollArea';

AppScrollArea.propTypes = {
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
	style: PropTypes.object,
	maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	/** 与 maxHeight 同高，避免首页等内容把 body 顶出第二条滚动条 */
	fillViewport: PropTypes.bool,
	autoHide: PropTypes.bool,
	/** 滚动时加 app-scroll-area--scrolling，停手后快速淡出（见 index.css） */
	highlightOnScroll: PropTypes.bool,
	scrollIdleMs: PropTypes.number,
	onMouseLeave: PropTypes.func,
	/** 传给 SimpleBar 实际滚动节点（如 react-select MenuList 的 innerRef） */
	scrollableNodeProps: PropTypes.object,
};

export default AppScrollArea;
